import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon, Modal, Button } from 'semantic-ui-react';
import { PageContent, PageHeader, Subtitle } from 'common/styles/page';
import PositionedModal from 'common/components/PositionedModal';
import OrganizationWizardStep1Properties from './OrganizationWizardStep1Properties';
import OrganizationWizardStep2CreateCategories from './OrganizationWizardStep2CreateCategories';
import OrganizationWizardStep3Categories from './OrganizationWizardStep3Categories';
import OrganizationWizardStep4Demographics from './OrganizationWizardStep4Demographics';
import OrganizationWizardStep5Survey from './OrganizationWizardStep5Survey';
import OrganizationWizardStep6OrgRecords from './OrganizationWizardStep6OrgRecords';
import OrganizationWizardStep7MemberRecords from './OrganizationWizardStep7MemberRecords';

export type WizardData = {
  organizationId?: number;
  name: string;
  time_zone: string;
  tier?: number;
  newCategories?: any[];
  savedCategoryIds?: number[];
  selectedCategories: any[];
  configuredCategories: any[];
  surveySettings: {
    default_survey_question: number | null;
    default_outcome_question: number | null;
    default_min_followup: number | null;
    default_max_followup: number | null;
    default_reminder_schedule: string;
  };
  organizationRecords: any[];
  memberRecords: any[];
};

type Step = {
  id: number;
  title: string;
  description: string;
  required: boolean;
  component: React.ComponentType<StepComponentProps>;
};

export type StepComponentProps = {
  data: WizardData;
  onDataChange: (updates: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
};

const STEPS: Step[] = [
  {
    id: 1,
    title: 'Organization',
    description: 'Basic organization information',
    required: true,
    component: OrganizationWizardStep1Properties,
  },
  {
    id: 2,
    title: 'Categories',
    description: 'Create organization categories',
    required: true,
    component: OrganizationWizardStep2CreateCategories,
  },
  {
    id: 3,
    title: 'Demographics',
    description: 'Select demographic categories',
    required: true,
    component: OrganizationWizardStep3Categories,
  },
  {
    id: 4,
    title: 'Configuration',
    description: 'Configure survey categories',
    required: true,
    component: OrganizationWizardStep4Demographics,
  },
  {
    id: 5,
    title: 'Survey',
    description: 'Configure survey settings',
    required: true,
    component: OrganizationWizardStep5Survey,
  },
  {
    id: 6,
    title: 'Org. Records',
    description: 'Custom organization fields',
    required: false,
    component: OrganizationWizardStep6OrgRecords,
  },
  {
    id: 7,
    title: 'Member Records',
    description: 'Custom member fields',
    required: false,
    component: OrganizationWizardStep7MemberRecords,
  },
];

const OrganizationWizard: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [wizardData, setWizardData] = useState<WizardData>({
    name: '',
    time_zone: 'US/Eastern',
    tier: 1,
    newCategories: [],
    selectedCategories: [],
    configuredCategories: [],
    surveySettings: {
      default_survey_question: null,
      default_outcome_question: null,
      default_min_followup: 2,
      default_max_followup: 2,
      default_reminder_schedule: '0 9 * * 1,3,5',
    },
    organizationRecords: [],
    memberRecords: [],
  });

  const handleDataChange = (updates: Partial<WizardData>) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      // Wizard complete - navigate to org edit page
      if (wizardData.organizationId) {
        navigate(`/settings/admin/organization/${wizardData.organizationId}/edit`);
      } else {
        navigate('/settings/admin/organization');
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepId: number) => {
    // Allow navigation to completed steps or next step
    if (completedSteps.has(stepId - 1) || stepId === 1) {
      setCurrentStep(stepId);
    }
  };

  const handleCancel = () => {
    setCancelModalOpen(true);
  };

  const confirmCancel = () => {
    setCancelModalOpen(false);
    navigate('/settings/admin/organization');
  };

  const currentStepData = STEPS[currentStep - 1];
  const StepComponent = currentStepData.component;

  return (
    <PageContent>
      <HeaderContainer>
        <PageHeader title="Create New Organization" />
        <StepInfo>  
          Step {currentStep} of {STEPS.length}
        </StepInfo>
      </HeaderContainer>
      <HeaderDivider />

      <WizardContainer>
        <Stepper>
          {STEPS.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = completedSteps.has(step.id);
            const isClickable = completedSteps.has(step.id - 1) || step.id === 1;

            return (
              <StepItem key={step.id}>
                <StepIndicator
                  $active={isActive}
                  $completed={isCompleted}
                  $clickable={isClickable}
                  onClick={() => isClickable && handleStepClick(step.id)}
                >
                  {isCompleted ? (
                    <Icon name="check" />
                  ) : (
                    <span>{step.id}</span>
                  )}
                </StepIndicator>
                <StepContent>
                  <StepTitle $active={isActive}>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
                {index < STEPS.length - 1 && <StepConnector $completed={isCompleted} />}
              </StepItem>
            );
          })}
        </Stepper>

        <StepContentArea>
          <StepComponent
            data={wizardData}
            onDataChange={handleDataChange}
            onNext={handleNext}
            onBack={handleBack}
            onCancel={handleCancel}
            isFirstStep={currentStep === 1}
            isLastStep={currentStep === STEPS.length}
          />
        </StepContentArea>
      </WizardContainer>

      <PositionedModal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)} size="small">
        <Modal.Header>Cancel Setup</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to cancel? Your progress will be lost.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCancelModalOpen(false)}>
            Continue Setup
          </Button>
          <Button negative onClick={confirmCancel}>
            Cancel Setup
          </Button>
        </Modal.Actions>
      </PositionedModal>
    </PageContent>
  );
};

export default OrganizationWizard;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const HeaderDivider = styled.div`
  height: 1px;
  background-color: #e0e0e0;
`;

const WizardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px; 
`;

const Stepper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  position: relative;
  background: #f8f9fa;
  padding: 24px 32px;
  border-radius: 8px;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
`;

const StepIndicator = styled.div<{ $active: boolean; $completed: boolean; $clickable: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  background-color: ${props =>
    props.$active ? '#2185d0' : props.$completed ? '#21ba45' : '#e0e0e0'};
  color: ${props => (props.$active || props.$completed ? '#fff' : '#666')};
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  transition: all 0.3s ease;
  z-index: 2;

  &:hover {
    ${props =>
      props.$clickable &&
      `
      transform: scale(1.1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    `}
  }

  i.icon {
    margin: 0;
  }
`;

const StepContent = styled.div`
  text-align: center;
  margin-top: 12px;
  max-width: 150px;
`;

const StepTitle = styled.div<{ $active: boolean }>`
  font-size: 14px;
  font-weight: ${props => (props.$active ? '600' : '500')};
  color: ${props => (props.$active ? '#2185d0' : '#333')};
  margin-bottom: 4px;
`;

const StepDescription = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.3;
`;

const RequiredBadge = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  background-color: #fbbd08;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  text-transform: uppercase;
`;

const StepConnector = styled.div<{ $completed: boolean }>`
  position: absolute;
  top: 20px;
  left: calc(50% + 20px);
  right: calc(-50% + 20px);
  height: 2px;
  background-color: ${props => (props.$completed ? '#21ba45' : '#e0e0e0')};
  z-index: 1;
`;

const StepContentArea = styled.div`
  background: #fff;
  border-radius: 8px;
  padding-left: 32px;
  min-height: 400px;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }
`;

const StepInfo = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  white-space: nowrap;
`;
